import { useId } from "react";

type Variant = "dark" | "light";

interface Props {
  variant?: Variant;
  markOnly?: boolean;
  height?: number;
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
}

/**
 * SerpDeskLogo — single source of truth for the brand mark.
 *
 * Mark geometry mirrors public/favicon.svg exactly:
 *   - Rounded square (rx=10 in lockup, rx=14 in mark-only)
 *   - Linear gradient #7c5cfc → #f24e80
 *   - Cubic-bezier S-curve M22 38 C22 30 42 30 42 22 (scaled to fit)
 *   - Two endpoint dots at the curve's start and end
 *
 * Variants:
 *   <SerpDeskLogo />                  full lockup, white wordmark (dark backgrounds)
 *   <SerpDeskLogo variant="light" />  full lockup, near-black wordmark (light backgrounds)
 *   <SerpDeskLogo markOnly />         symbol only, no wordmark
 *   <SerpDeskLogo height={N} />       scale proportionally
 */
export function SerpDeskLogo({
  variant = "dark",
  markOnly = false,
  height = 32,
  className,
  style,
  ariaLabel = "SerpDesk",
}: Props) {
  const gradId = `sd-${useId().replace(/:/g, "")}`;
  const wordmarkColor = variant === "light" ? "#0e1219" : "#ffffff";

  if (markOnly) {
    // 64×64 viewBox to match favicon.svg exactly.
    const width = height;
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 64 64"
        role="img"
        aria-label={ariaLabel}
        className={className}
        style={style}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#7c5cfc" />
            <stop offset="100%" stopColor="#f24e80" />
          </linearGradient>
        </defs>
        <rect width="64" height="64" rx="14" fill={`url(#${gradId})`} />
        <path
          d="M22 38 C22 30 42 30 42 22"
          stroke="#ffffff"
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
        />
        <circle cx="22" cy="38" r="4" fill="#ffffff" />
        <circle cx="42" cy="22" r="4" fill="#ffffff" />
      </svg>
    );
  }

  // Lockup: 280×56 viewBox. Mark is 44×44 with y-offset 6 to vertically
  // center against the 28px wordmark. Mark coords are favicon coords
  // mapped from the 64×64 viewBox into a 44×44 box (factor 44/64 = 0.6875),
  // which gives endpoints (15, 26) and (29, 15) and CPs (15, 21) and (29, 21).
  const width = (height * 280) / 56;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 280 56"
      role="img"
      aria-label={ariaLabel}
      className={className}
      style={style}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#7c5cfc" />
          <stop offset="100%" stopColor="#f24e80" />
        </linearGradient>
      </defs>
      <g transform="translate(0 6)">
        <rect width="44" height="44" rx="10" fill={`url(#${gradId})`} />
        <path
          d="M15 26 C15 21 29 21 29 15"
          stroke="#ffffff"
          strokeWidth="2.75"
          fill="none"
          strokeLinecap="round"
        />
        <circle cx="15" cy="26" r="2.75" fill="#ffffff" />
        <circle cx="29" cy="15" r="2.75" fill="#ffffff" />
      </g>
      <text
        x="60"
        y="38"
        fontFamily="Outfit, system-ui, -apple-system, BlinkMacSystemFont, sans-serif"
        fontWeight="700"
        fontSize="28"
        fill={wordmarkColor}
        letterSpacing="-0.56"
      >
        SerpDesk
      </text>
    </svg>
  );
}

export default SerpDeskLogo;
